import { useEntityStore } from '@ContractBuilder/store';
import { isEndorsementData } from '@ContractBuilder/types';
import type { ResourceSection } from '@domain/schemas/contract.schema';
import { ENDORSEMENT_DETAILS_SENTINEL } from '@domain/schemas/endorsement-summary.schema';
import { getSortedEntries, hasSummarySections, isSummarySection } from '@root/helpers';
import { useDeepCompareMemo } from '@src/hooks';
import { get } from 'lodash-es';

import { type EndorsementDetailsNavItem, type NavItem } from '../types';

/**
 * Returns an ordered list of navigation items for the endorsement summary view.
 * Summary sections are returned as-is; all dynamically-computed endorsement change
 * sections are collapsed into a single EndorsementDetailsNavItem, positioned at the
 * location of the first change section in the expanded summary.layout.
 */
export function useEndorsementNavSections(): NavItem[] {
  const submission = useEntityStore(({ submission }) => submission);

  return useDeepCompareMemo(() => {
    if (!submission || !isEndorsementData(submission)) {
      return [];
    }

    const layout: string[] = get(submission, 'summary.layout', []);
    // Summary blocks are served with block defaults filled in; the summary response schema under-describes them.
    const allSections = get(submission, 'summary.sections', []) as ResourceSection[];

    const sortedSections = getSortedEntries(layout, allSections);

    if (submission.status === 'DRAFT' && !hasSummarySections(sortedSections)) {
      return [];
    }

    const endorsementDetailsSections = sortedSections.filter((s) => !isSummarySection(s));
    const firstChangeIdx = sortedSections.findIndex((s) => !isSummarySection(s));

    const endorsementDetailsNavItem: EndorsementDetailsNavItem = {
      id: ENDORSEMENT_DETAILS_SENTINEL,
      label: 'Material Changes',
      endorsementDetailsSections,
    };

    if (firstChangeIdx === -1) {
      const sentinelIdx = layout.indexOf(ENDORSEMENT_DETAILS_SENTINEL);

      if (sentinelIdx === -1) {
        return sortedSections; // legacy pre-sentinel layout; heals on first reorder
      }

      const idsBeforeSentinel = new Set(layout.slice(0, sentinelIdx));
      const before = sortedSections.filter((s) => idsBeforeSentinel.has(s.id));
      const after = sortedSections.filter((s) => !idsBeforeSentinel.has(s.id));
      return [...before, endorsementDetailsNavItem, ...after];
    }

    const before = sortedSections.slice(0, firstChangeIdx).filter(isSummarySection);
    const after = sortedSections.slice(firstChangeIdx).filter(isSummarySection);

    return [...before, endorsementDetailsNavItem, ...after];
  }, [submission]);
}
